"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import Schedule from "@/app/_components/Schedule/Schedule";
import {
  getEventSchedule,
  getMySchedule,
  addMyScheduleToDB,
  removeMyScheduleFromDB,
} from "@/helpers/server-actions/events-collection";
import {
  EventItem,
  EventItemType,
  MySchedule,
  Vertical,
} from "@/models/event-item";
import { AppDispatch, useAppSelector } from "@/redux/store";
import {
  setSchedule,
  setMySchedule,
  fetchEventSchedule,
} from "@/redux/features/schedule-slice";
import { filterByUniqueDate } from "@/helpers/utils/utility-functions";
import DateFilter from "@/app/_components/Schedule/DateFilter";
import ScheduleFilter from "@/app/_components/Schedule/ScheduleFilter";
import BottomSheet from "../_components/BottomSheet/BottomSheet";
import Image from "next/image";
import { useSearchParams } from "next/navigation";
import styles from "@/app/_components/Schedule/Schedule.module.css";
import { useDispatch } from "react-redux";
import { motion } from "framer-motion";

export default function SchedulePage() {
  const [filteredEvents, setFilteredEvents] = useState<EventItem[] | null>(
    null
  );
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [dateEvents, setDateEvents] = useState<EventItem[]>([]);
  const searchParams = useSearchParams();
  const backgroundGradient = useAppSelector(
    (state) => state.dashboardReducer.gradient
  );
  const eventScheduleData = useAppSelector(
    (state) => state.scheduleReducer.schedule
  );
  const myScheduleData = useAppSelector(
    (state) => state.scheduleReducer.mySchedule
  );
  const dispatch = useDispatch<AppDispatch>();
  const selectedEventId = searchParams.get("eventId") || "";
  const [scheduleRefs, setScheduleRefs] = useState<{
    [key: string]: React.RefObject<HTMLDivElement>;
  }>({});
  const [selectedVerticals, setSelectedVerticals] = useState<Vertical[]>([]);
  const [selectedEventType, setSelectedEventType] =
    useState<EventItemType | null>(null);
  const [selectedMySchedule, setSelectedMySchedule] = useState<boolean>(false);
  const user = useAppSelector((state) => state.appReducer.userData);
  const [isScrolled, setIsScrolled] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 640);
  const [showRecommendedVideo, setShowRecommendedVideo] = useState(true);
  const [autoPlayIndex, setAutoPlayIndex] = useState(-1);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      return;
    }
    const checkScroll = () => {
      const isScrolling = scrollContainer.scrollTop > 0;
      if (isScrolling && !isScrolled) {
        setIsScrolled(true);
      } else if (!isScrolling && isScrolled) {
        setIsScrolled(false);
      }
    };

    scrollContainer.addEventListener("scroll", checkScroll);
    return () => scrollContainer.removeEventListener("scroll", checkScroll);
  }, [isScrolled]);

  useEffect(() => {
    if (eventScheduleData.length === 0) {
      dispatch(fetchEventSchedule());
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 640);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (myScheduleData.length === 0 && user) {
      getMySchedule(user.uid).then((data) => {
        dispatch(setMySchedule(data));
      });
    }
  }, []);

  const applyFiltersAndSetEvents = useCallback(
    (
      index: number,
      verticals: Vertical[],
      selectedMySchedule: boolean,
      eventScheduleData: EventItem[]
    ) => {
      if (!eventScheduleData) return;
      const filteredEvents = filterByUniqueDate(eventScheduleData, index);
      let filteredTemp = filteredEvents;
      if (verticals.length > 0 || selectedMySchedule) {
        filteredTemp = filteredEvents.filter((event) => {
          const noVerticalsAndNoMySchedule =
            verticals.length === 0 && !selectedMySchedule;
          const verticalIncludedAndNoMySchedule =
            verticals.includes(event.vertical) && !selectedMySchedule;
          const myScheduleSelectedAndEventInMySchedule =
            selectedMySchedule &&
            myScheduleData.findIndex(
              (mySchedule) => mySchedule.id === event.id
            ) !== -1;
          const verticalIncluded = verticals.includes(event.vertical);

          return (
            noVerticalsAndNoMySchedule ||
            verticalIncludedAndNoMySchedule ||
            myScheduleSelectedAndEventInMySchedule ||
            verticalIncluded
          );
        });
      }
      setFilteredEvents(filteredTemp);
    },
    [myScheduleData]
  );

  useEffect(() => {
    const setupScheduleRefs = () => {
      const scheduleRefsTemp = eventScheduleData.reduce((acc: any, event) => {
        acc[event.id] = React.createRef();
        return acc;
      }, {});
      setScheduleRefs(scheduleRefsTemp);
    };

    if (eventScheduleData.length > 0) {
      setupScheduleRefs();
    }
  }, [eventScheduleData]);

  useEffect(() => {
    if (dateEvents.length === 0 && eventScheduleData.length > 0) {
      // C-Tribe Festival 2027: August 12-17
      const festivalStart = new Date("2027-08-12T00:00:00-06:00").getTime();
      const festivalEnd = new Date("2027-08-18T00:00:00-06:00").getTime(); // Day after last day

      const filteredByYear = eventScheduleData.filter((event) => {
        // Must have festivalYear 2027
        if (event.festivalYear !== 2027) {
          return false;
        }
        // Must be within festival date range (Aug 12-17)
        const eventTime = event.startTime.seconds * 1000;
        return eventTime >= festivalStart && eventTime < festivalEnd;
      });
      const sortedEvents = [...filteredByYear].sort((a, b) => {
        const dateTimeA = new Date(a.startTime.seconds * 1000);
        const dateTimeB = new Date(b.startTime.seconds * 1000);
        return dateTimeA.getTime() - dateTimeB.getTime();
      });
      setDateEvents(sortedEvents);
    }
  }, [dateEvents.length, eventScheduleData]);

  useEffect(() => {
    applyFiltersAndSetEvents(
      selectedIndex,
      selectedVerticals,
      selectedMySchedule,
      dateEvents
    );
  }, [selectedIndex, selectedVerticals, selectedMySchedule, dateEvents]);

  useEffect(() => {
    if (selectedEventId && scheduleRefs) {
      const selectedEventIndex = filteredEvents?.findIndex(
        (event) => event.id === selectedEventId
      );
      if (selectedEventIndex === -1) return;

      const selectedEventRef = scheduleRefs[selectedEventId];
      if (selectedEventRef && selectedEventRef.current) {
        const selectedElement = selectedEventRef.current;
        selectedElement.scrollIntoView({ behavior: "smooth", block: "center" });

        setTimeout(() => {
          selectedElement.classList.add(
            "scale-105",
            "transition-transform",
            "duration-500"
          );
          setTimeout(() => {
            selectedElement.classList.remove("scale-105");
            selectedElement.classList.add("scale-100");
          }, 1000);
        }, 500);
      }
    }
  }, [selectedEventId, scheduleRefs, filteredEvents]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            console.log("autoPlayIndex", entry.target.id);
            setAutoPlayIndex(parseInt(entry.target.id));
          }
        });
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.5,
      }
    );

    if (scheduleRefs) {
      Object.values(scheduleRefs).forEach((ref) => {
        if (ref && ref.current) {
          observer.observe(ref.current);
          console.log("Observing", ref.current);
        }
      });

      return () => {
        Object.values(scheduleRefs).forEach((ref) => {
          if (ref && ref.current) {
            observer.unobserve(ref.current);
          }
        });
      };
    }
  }, [scheduleRefs]);

  const handleDateEventsChange = (index: number) => {
    setSelectedIndex(index);
  };

  const updateMySchedule = async (event: EventItem) => {
    const mySchedule = myScheduleData;
    const myScheduleIndex = mySchedule.findIndex(
      (mySchedule) => mySchedule.id === event.id
    );

    if (myScheduleIndex !== -1) {
      const mutableMySchedule = [...mySchedule];
      mutableMySchedule.splice(myScheduleIndex, 1);
      dispatch(setMySchedule(mutableMySchedule));
      if (user) {
        await removeMyScheduleFromDB(event.id, user.uid);
      }
    } else {
      const mutableMySchedule = [...mySchedule];
      mutableMySchedule.push({
        id: event.id,
        timestamp: new Date(),
      } as MySchedule);
      dispatch(setMySchedule(mutableMySchedule));
      if (user) {
        await addMyScheduleToDB(event.id, user.uid);
      }
    }
  };

  return (
    <div>
      <BottomSheet
        isOpen={isSmallScreen && isFilterOpen}
        height="80%"
        onClose={() => setIsFilterOpen(false)}
      >
        <div className="w-full px-6 pt-8 pb-2.5 bg-white border-b border-stone-300 items-start gap-[261px] inline-flex rounded-t-2xl justify-between">
          <div className="text-zinc-800 text-base font-semibold">Filter</div>
          <Image
            className="cursor-pointer"
            src="/images/search-clear.svg"
            alt="close"
            width={13}
            height={13}
            onClick={() => setIsFilterOpen(false)}
          />
        </div>
        <ScheduleFilter
          selectedEventType={selectedEventType || "knowledge"}
          selectedVertical={selectedVerticals}
          selectedMySchedule={selectedMySchedule}
          shouldShowApplyButton={true}
          onApply={() => setIsFilterOpen(false)}
          setSelectedEventType={(eventType) => setSelectedEventType(eventType)}
          setSelectedVertical={(verticals) => setSelectedVerticals(verticals)}
          setSelectedMySchedule={(value) => setSelectedMySchedule(value)}
        />
      </BottomSheet>

      <div
        ref={scrollContainerRef}
        className={`flex flex-col ${backgroundGradient} overflow-auto max-h-screen`}
      >
        <div
          className={`md:pt-9 pt-20 sticky top-0 z-10 ${
            isScrolled ? "bg-white bg-opacity-40 backdrop-blur" : ""
          }`}
        >
          <div className="flex px-[1.875rem] pb-3 md:pb-5 justify-between">
            <div className="flex">
              <p className={styles["labeldate-typo"]}>2026</p>
              <p className={styles["labelname-typo"]}>Schedule</p>
            </div>
            <div className="block sm:hidden border-solid border-[1px] border-[var(--Neutrals-Main-Neutral---Limed-Spruce)] py-[3px] px-[16px] rounded-full flex-shrink-0">
              <button
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className="text-[var(--Neutrals-Secondary-Dark-Neutral---Thunder)] font-bold text-[0.75rem] uppercase tracking-[1.44px]"
              >
                Filter
              </button>
            </div>
          </div>
          <DateFilter
            selected={selectedIndex}
            getdata={handleDateEventsChange}
            events={dateEvents}
            seletectedEventId={selectedEventId}
          />
        </div>
        <div className="flex flex-row">
          <div className={`hidden sm:block`}>
            <ScheduleFilter
              selectedEventType={selectedEventType || "knowledge"}
              selectedVertical={selectedVerticals}
              selectedMySchedule={selectedMySchedule}
              shouldShowApplyButton={false}
              setSelectedEventType={(eventType) =>
                setSelectedEventType(eventType)
              }
              setSelectedVertical={(verticals) =>
                setSelectedVerticals(verticals)
              }
              setSelectedMySchedule={() =>
                setSelectedMySchedule(!selectedMySchedule)
              }
            />
          </div>
          {filteredEvents && filteredEvents.length > 0 && (
            <div className="flex flex-col justify-self-center md:justify-start md:pl-8 md:items-start items-center pb-20 max-w-[100vw]">
              {filteredEvents.map((filteredEvent, index) => (
                <motion.div
                  key={filteredEvent.id}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Schedule
                    key={filteredEvent.id}
                    autoExpand={autoPlayIndex === index}
                    onMyScheduleClick={updateMySchedule}
                    isMySchedule={myScheduleData.some(
                      (mySchedule) => mySchedule.id === filteredEvent.id
                    )}
                    eventItem={filteredEvent}
                    ref={scheduleRefs[filteredEvent.id]}
                    showRecommendedVideo={showRecommendedVideo}
                    setShowRecommendedVideo={setShowRecommendedVideo}
                    id={index.toString()}
                  />
                </motion.div>
              ))}
            </div>
          )}
          {filteredEvents && filteredEvents.length === 0 && (
            <div className="flex justify-center items-center w-[100vw] h-[100vh]">
              <p className="text-base font-bold">No results to share</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
